import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Autocomplete, Box, Button, Chip,
  Divider, FormControlLabel, IconButton, MenuItem, Pagination, Skeleton, Stack, Switch,
  TextField, Tooltip, Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { AppLayout } from "@/components/AppLayout";
import { fetchVendorRules, updateVendorRule, type VendorRule } from "@/api/vendorRules";
import { fetchReferenceData, type ReferenceData, type RefOption, type ItemOption } from "@/api/referenceData";

type Defaults = Record<string, unknown>;

const PAGE_SIZE_OPTIONS = [ 10, 25, 50, 100 ];

export function SupportedVendorsPage() {
  const rules = useQuery({ queryKey: ["vendor_rules"], queryFn: fetchVendorRules });
  const ref = useQuery({ queryKey: ["reference_data"], queryFn: fetchReferenceData });
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = rules.data ?? [];
    if (!q) return all;
    return all.filter((r) =>
      r.name.toLowerCase().includes(q) ||
      String(r.netsuite_vendor_id ?? "").includes(q) ||
      (r.default && "default".includes(q))
    );
  }, [rules.data, query]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  // Keep the page in range as the filtered set / page size changes.
  const current = Math.min(page, pageCount);
  useEffect(() => { if (page !== current) setPage(current); }, [current, page]);
  const paged = filtered.slice((current - 1) * pageSize, current * pageSize);
  const rangeStart = filtered.length === 0 ? 0 : (current - 1) * pageSize + 1;
  const rangeEnd = Math.min(current * pageSize, filtered.length);

  return (
    <AppLayout>
      <Typography variant="h5" gutterBottom>Supported Vendors</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The OCR + AI rules used to turn each vendor’s invoices into a NetSuite bill, and how each
        bill’s Approver, Class, and Location are coded. The <strong>Default</strong> ruleset applies
        to any vendor without a bespoke row.
      </Typography>
      {rules.isLoading || ref.isLoading ? (
        <Stack spacing={1}>
          <Skeleton variant="rounded" height={40} sx={{ mb: 0.5 }} /> {/* search box */}
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={48} />
          ))}
        </Stack>
      ) : (
        <>
          <TextField
            size="small" fullWidth placeholder="Search vendors by name or NetSuite id…"
            value={query} onChange={(e) => { setQuery(e.target.value); setPage(1); }} sx={{ mb: 1.5 }}
            slotProps={{
              input: {
                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />,
                endAdornment: query ? (
                  <IconButton size="small" onClick={() => { setQuery(""); setPage(1); }} aria-label="Clear search">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ) : null,
              },
            }}
          />
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {filtered.length > 0
                ? `Showing ${rangeStart}–${rangeEnd} of ${filtered.length}${query ? " matching" : ""} vendor rules`
                : ""}
            </Typography>
            <TextField
              select size="small" label="Per page" value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              sx={{ minWidth: 100 }}
            >
              {PAGE_SIZE_OPTIONS.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
            </TextField>
          </Stack>
          <Stack spacing={1}>
            {paged.map((rule) => (
              <RuleRow key={rule.id} rule={rule} ref_={ref.data as ReferenceData} />
            ))}
            {filtered.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: "center" }}>
                No vendors match “{query}”.
              </Typography>
            )}
          </Stack>
          {pageCount > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination
                count={pageCount} page={current} onChange={(_, p) => setPage(p)}
                color="primary" shape="rounded"
              />
            </Box>
          )}
        </>
      )}
    </AppLayout>
  );
}

// ── small reusable field controls ──────────────────────────────────────────
function num(v: unknown): number | null {
  return typeof v === "number" ? v : v == null || v === "" ? null : Number(v);
}

function RefPicker({ label, options, value, onChange, hint }: {
  label: string; options: RefOption[]; value: number | null;
  onChange: (id: number | null) => void; hint?: string;
}) {
  const selected = options.find((o) => o.id === value) ?? null;
  return (
    <Autocomplete
      size="small" options={options} value={selected}
      getOptionLabel={(o) => o.name ?? String(o.id)}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      onChange={(_, o) => onChange(o ? o.id : null)}
      renderInput={(p) => <TextField {...p} label={label} helperText={hint} />}
      sx={{ minWidth: 240, flex: 1 }}
    />
  );
}

function ItemPicker({ label, options, value, onChange, hint }: {
  label: string; options: ItemOption[]; value: number | null;
  onChange: (id: number | null) => void; hint?: string;
}) {
  const selected = options.find((o) => o.id === value) ?? null;
  return (
    <Autocomplete
      size="small" options={options} value={selected}
      getOptionLabel={(o) => (o.vendor_code ? `${o.name} · ${o.vendor_code}` : o.name)}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      onChange={(_, o) => onChange(o ? o.id : null)}
      renderInput={(p) => <TextField {...p} label={label} helperText={hint} />}
      sx={{ minWidth: 240, flex: 1 }}
    />
  );
}

function SelectField({ label, options, value, onChange }: {
  label: string; options: RefOption[]; value: number | null; onChange: (id: number | null) => void;
}) {
  return (
    <TextField
      select size="small" label={label} value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      sx={{ minWidth: 200, flex: 1 }}
    >
      <MenuItem value=""><em>—</em></MenuItem>
      {options.map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
    </TextField>
  );
}

function ToggleRow({ label, hint, checked, onChange }: {
  label: string; hint: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <Tooltip title={hint} placement="right" arrow>
      <FormControlLabel
        sx={{ ml: 0, mr: 3 }}
        control={<Switch size="small" checked={checked} onChange={(e) => onChange(e.target.checked)} sx={{ ml: -0.75 }} />}
        label={<Typography variant="body2">{label}</Typography>}
      />
    </Tooltip>
  );
}

// Field content (labels/values) sits inset from the outlined-box border; align
// section headers and toggle rows to that same line so everything lines up.
const LABEL_PL = 1.75;

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <Typography variant="overline" color="text.secondary" sx={{ display: "block", pl: LABEL_PL }}>
      {children}
    </Typography>
  );
}

// ── the per-vendor editor ───────────────────────────────────────────────────
function RuleRow({ rule, ref_ }: { rule: VendorRule; ref_: ReferenceData }) {
  const qc = useQueryClient();
  const [instructions, setInstructions] = useState(rule.ai_instructions ?? "");
  const [defaults, setDefaults] = useState<Defaults>(rule.defaults ?? {});
  const [advanced, setAdvanced] = useState(JSON.stringify(rule.defaults ?? {}, null, 2));
  const [jsonError, setJsonError] = useState<string | null>(null);
  // Aliases edited as one-per-line text; saved as a trimmed string array.
  const [aliasText, setAliasText] = useState((rule.aliases ?? []).join("\n"));

  useEffect(() => {
    setInstructions(rule.ai_instructions ?? "");
    setDefaults(rule.defaults ?? {});
    setAdvanced(JSON.stringify(rule.defaults ?? {}, null, 2));
    setAliasText((rule.aliases ?? []).join("\n"));
    setJsonError(null);
  }, [rule.updated_at]);

  // Structured edits and the advanced JSON stay in sync: a structured change
  // updates both the object and the JSON text; a JSON edit reparses the object.
  const setKey = (key: string, value: unknown) => {
    const next = { ...defaults };
    if (value === null || value === undefined || value === "") delete next[key];
    else next[key] = value;
    setDefaults(next);
    setAdvanced(JSON.stringify(next, null, 2));
  };
  const onAdvanced = (text: string) => {
    setAdvanced(text);
    try {
      setDefaults(JSON.parse(text || "{}"));
      setJsonError(null);
    } catch (e) {
      setJsonError((e as Error).message);
    }
  };

  // A toggle that's absent from defaults reads as its documented default.
  const bool = (key: string, fallback = false) =>
    defaults[key] === undefined ? fallback : defaults[key] === true;

  const aliases = useMemo(
    () => aliasText.split("\n").map((s) => s.trim()).filter(Boolean),
    [aliasText]
  );

  const save = useMutation({
    mutationFn: () => updateVendorRule(rule.id, { ai_instructions: instructions, defaults, aliases }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["vendor_rules"] }),
  });

  const { employees, classes, locations, items, accounts } = ref_;
  const isExpense = num(defaults.expense_default_account_id) != null;

  const hasMap = useMemo(
    () => ["location_by_branch", "class_by_job_type", "approver_by_keyword"].some((k) => defaults[k]),
    [defaults]
  );

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexGrow: 1 }}>
          <Typography sx={{ flexGrow: 1 }}>{rule.name}</Typography>
          {rule.default && <Chip size="small" label="default" />}
          {rule.netsuite_vendor_id && <Chip size="small" variant="outlined" label={`vendor ${rule.netsuite_vendor_id}`} />}
          <Chip size="small" variant="outlined" label={`${rule.bill_count} drafts`} />
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2.5, pt: 1, pb: 2.5 }}>
        <Stack spacing={2.5}>
          {/* Approver routing */}
          <Box>
            <SectionLabel>Approver routing</SectionLabel>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
              <RefPicker
                label="Default approver" options={employees} value={num(defaults.approver_id)}
                onChange={(id) => setKey("approver_id", id)} hint="Fallback / service bills"
              />
              <RefPicker
                label="Commercial approver" options={employees} value={num(defaults.commercial_approver_id)}
                onChange={(id) => setKey("commercial_approver_id", id)} hint="Commercial jobs (optional)"
              />
            </Stack>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 0.5, pl: LABEL_PL }}>
              <ToggleRow
                label="Route to ATTN employee" checked={bool("attn_routing", true)}
                onChange={(v) => setKey("attn_routing", v)}
                hint="When the invoice prints an ATTN/Attention name that matches a NetSuite employee, route the approver to them (highest priority). On by default."
              />
              <ToggleRow
                label="Residential → regional ROM" checked={bool("residential_approver_from_rom")}
                onChange={(v) => setKey("residential_approver_from_rom", v)}
                hint="Residential, non-DLL bills route to the Regional Operations Manager for the bill's location, from the shared ROM roster."
              />
            </Stack>
          </Box>

          <Divider />

          {/* Class / Location coding */}
          <Box>
            <SectionLabel>Class &amp; location</SectionLabel>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
              <SelectField label="Default class" options={classes} value={num(defaults.class_id)}
                onChange={(id) => setKey("class_id", id)} />
              <SelectField label="Default location" options={locations} value={num(defaults.location_id)}
                onChange={(id) => setKey("location_id", id)} />
              <SelectField label="Commercial location" options={locations} value={num(defaults.commercial_location_id)}
                onChange={(id) => setKey("commercial_location_id", id)} />
            </Stack>
          </Box>

          <Divider />

          {/* Line coding */}
          <Box>
            <SectionLabel>Line coding</SectionLabel>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
              <ItemPicker label="Generic fallback item" options={items} value={num(defaults.item_id)}
                onChange={(id) => setKey("item_id", id)} hint="Item used for lines that don't match" />
              <RefPicker label="Expense-default account" options={accounts} value={num(defaults.expense_default_account_id)}
                onChange={(id) => setKey("expense_default_account_id", id)}
                hint="Set to code the WHOLE bill to one GL account (expense vendors)" />
            </Stack>
            {isExpense ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                This vendor codes every line to the expense-default account above — item matching and
                history suggestions are skipped.
              </Alert>
            ) : (
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 0.5, pl: LABEL_PL }}>
                <ToggleRow label="Match inventory items" checked={bool("match_items")}
                  onChange={(v) => setKey("match_items", v)}
                  hint="Map stock lines to real NetSuite items by name + vendor code." />
                <ToggleRow label="Suggest items from history" checked={bool("suggest_items_from_history")}
                  onChange={(v) => setKey("suggest_items_from_history", v)}
                  hint="For unmatched lines, suggest the item most-used on similar past memos (same vendor + class)." />
                <ToggleRow label="Tax/shipping → dominant account" checked={bool("tax_shipping_follows_dominant_account")}
                  onChange={(v) => setKey("tax_shipping_follows_dominant_account", v)}
                  hint="Post sales tax & freight to the expense account of the largest item line, on the Expenses tab (non-PO bills)." />
              </Stack>
            )}
          </Box>

          <Divider />

          {/* Vendor name aliases (skipped on the default rule — it has no vendor) */}
          {!rule.default && (
            <Box>
              <SectionLabel>Vendor name aliases</SectionLabel>
              <TextField
                label="Aliases (one per line)"
                multiline minRows={2} fullWidth value={aliasText}
                onChange={(e) => setAliasText(e.target.value)}
                helperText="Names an invoice may print for this vendor that don't match its NetSuite name (brands / DBAs), e.g. “Greentech Renewables” → CED. Used to resolve the vendor from the OCR'd invoice."
                sx={{ mt: 0.5 }}
              />
            </Box>
          )}

          {/* AI instructions */}
          <TextField
            label="AI instructions (vendor-specific prompt rules)"
            multiline minRows={5} fullWidth value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
          />

          {/* Advanced: raw JSON, holds the maps (branch→location, keyword routes, class-by-job) */}
          <Accordion variant="outlined" disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" color="text.secondary">
                Advanced (raw JSON){hasMap ? " — includes branch/keyword maps" : ""}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                label="All coding defaults (JSON)" multiline minRows={6} fullWidth
                value={advanced} onChange={(e) => onAdvanced(e.target.value)}
                error={!!jsonError} helperText={jsonError ?? "Edits here sync with the fields above."}
                slotProps={{ input: { sx: { fontFamily: "monospace", fontSize: 12 } } }}
              />
            </AccordionDetails>
          </Accordion>

          {save.isError && !jsonError && <Alert severity="error">Save failed.</Alert>}
          <Box sx={{ textAlign: "right" }}>
            <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending || !!jsonError}>
              {save.isPending ? "Saving…" : "Save rules"}
            </Button>
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
